import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Navigate, Route, Routes, useNavigate } from 'react-router';
import { useAuth } from './auth/AuthProvider';
import { StackLayout, TabLayout } from './components/AppShell';
import { ErrorState, LoadingScreen } from './components/ui';
import { errorMessage } from './lib/format';
import { routeForStartParam, telegramLinkFor } from './lib/links';
import { getStartParam, isInTelegram, tg } from './lib/telegram';
import { BalancesPage } from './pages/BalancesPage';
import { CreateGroupPage } from './pages/CreateGroupPage';
import { DashboardPage } from './pages/DashboardPage';
import { ExpenseDetailPage } from './pages/ExpenseDetailPage';
import { ExpenseFormPage } from './pages/ExpenseFormPage';
import { GroupDetailPage } from './pages/GroupDetailPage';
import { GroupsPage } from './pages/GroupsPage';
import { InsightsPage } from './pages/InsightsPage';
import { JoinPage } from './pages/JoinPage';
import { LandingPage } from './pages/LandingPage';
import { MembersPage } from './pages/MembersPage';
import { ProfilePage } from './pages/ProfilePage';
import { SettlePage } from './pages/SettlePage';
import { WelcomePage } from './pages/WelcomePage';
import { Logo } from './components/Logo';

const ONBOARDED_KEY = 'expenditure.onboarded';

function readOnboarded(): boolean {
  try {
    return localStorage.getItem(ONBOARDED_KEY) === '1';
  } catch {
    return false;
  }
}

export function App() {
  const { t } = useTranslation();
  const { state, retry } = useAuth();
  const [onboarded, setOnboarded] = useState(readOnboarded);

  if (state.status === 'loading') return <LoadingScreen />;
  if (state.status === 'error') {
    return (
      <div className="center-screen">
        <ErrorState message={errorMessage(state.error, t)} onRetry={retry} />
      </div>
    );
  }
  if (state.status === 'deleted') return <AccountDeleted />;
  if (state.status === 'unauthenticated') {
    // The web address itself shows what the app is; links into it (invites) go to Telegram.
    return window.location.pathname === '/' ? <LandingPage /> : <OpenInTelegram />;
  }

  if (state.isNewUser && !onboarded) {
    return (
      <WelcomePage
        onDone={() => {
          try {
            localStorage.setItem(ONBOARDED_KEY, '1');
          } catch {
            // ignore
          }
          setOnboarded(true);
        }}
      />
    );
  }

  return (
    <BrowserRouter>
      <StartParamRedirect param={state.startParam ?? getStartParam()} />
      <Routes>
        <Route element={<TabLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="groups" element={<GroupsPage />} />
          <Route path="profile" element={<ProfilePage />} />
        </Route>
        <Route element={<StackLayout />}>
          <Route path="add" element={<GroupsPage pickForExpense />} />
          <Route path="groups/new" element={<CreateGroupPage />} />
          <Route path="groups/:groupId" element={<GroupDetailPage />} />
          <Route path="groups/:groupId/members" element={<MembersPage />} />
          <Route path="groups/:groupId/balances" element={<BalancesPage />} />
          <Route path="groups/:groupId/settle" element={<SettlePage />} />
          <Route path="groups/:groupId/insights" element={<InsightsPage />} />
          <Route path="groups/:groupId/expenses/new" element={<ExpenseFormPage />} />
          <Route path="groups/:groupId/expenses/:expenseId" element={<ExpenseDetailPage />} />
          <Route path="groups/:groupId/expenses/:expenseId/edit" element={<ExpenseFormPage />} />
          <Route path="join/:code" element={<JoinPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

/** Handles deep links such as t.me/<bot>/<app>?startapp=join_<code> once per launch. */
function StartParamRedirect({ param }: { param: string | null | undefined }) {
  const navigate = useNavigate();
  const handled = useRef(false);
  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const route = routeForStartParam(param);
    if (route) void navigate(route, { replace: true });
  }, [param, navigate]);
  return null;
}

function AccountDeleted() {
  const { t } = useTranslation();
  return (
    <div className="center-screen">
      <div className="stack" style={{ alignItems: 'center', maxWidth: 320, textAlign: 'center' }}>
        <Logo size={72} className="logo-mark" />
        <h1 className="page-title">{t('profile.deletedTitle')}</h1>
        <p className="muted" style={{ margin: 0 }}>
          {t('profile.deletedBody')}
        </p>
        {isInTelegram() && (
          <button className="btn btn--primary" onClick={() => tg?.close()}>
            {t('common.close')}
          </button>
        )}
      </div>
    </div>
  );
}

function OpenInTelegram() {
  const { t } = useTranslation();
  const bot = import.meta.env.VITE_BOT_USERNAME as string | undefined;
  // A link opened in a normal browser (e.g. an invite) can't sign anyone in
  // here: continue in Telegram, keeping the join code.
  const target = telegramLinkFor(window.location.pathname, {
    botUsername: bot,
    miniAppLink: import.meta.env.VITE_MINI_APP_LINK as string | undefined,
  });
  useEffect(() => {
    if (target) window.location.replace(target);
  }, [target]);
  return (
    <div className="center-screen">
      <div className="stack" style={{ alignItems: 'center', maxWidth: 320 }}>
        <Logo size={72} className="logo-mark" />
        <h1 className="page-title">{t('openInTelegram.title')}</h1>
        <p className="muted" style={{ margin: 0 }}>
          {t('openInTelegram.body')}
        </p>
        {target && (
          <a className="btn btn--primary" href={target}>
            {t('openInTelegram.button')}
          </a>
        )}
      </div>
    </div>
  );
}
